/**
 * Validación en vivo del borrador de plantilla contra el backend (la única fuente de verdad de las
 * reglas de Meta).
 *
 * - Debounce de 400 ms desde el último cambio real del borrador (por huella, no por referencia).
 * - Cada pedido lleva un número de secuencia: una respuesta que llega con un número menor que el
 *   último pedido se descarta. Sin eso, una validación lenta del texto viejo podía llegar después
 *   de la del texto nuevo y habilitar el botón de aprobación con reglas que ya no aplican.
 *   (`req()` no soporta AbortController.)
 * - `validando` pasa a true apenas cambia el borrador, no cuando sale el pedido: el botón de
 *   aprobación se apaga en el mismo render en que el operador tipea.
 * - Si el pedido falla, `resultado` queda el anterior pero con su `huellaResultado` vieja, así
 *   `puedeEnviarAAprobacion` da false (fail-closed) hasta que una validación nueva salga bien.
 * - `reintentar()` vuelve a validar la huella actual sin que el operador toque un campo (después
 *   de un corte de red o un 5xx, lo único que cambió es el servidor, no el borrador).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { PlantillaBorrador, ResultadoValidacionApi } from "../types";
import { oficialApi } from "../api";
import { huellaBorrador } from "../logic/plantilla";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";

export const DEBOUNCE_VALIDACION_MS = 400;

export function useValidacionPlantilla(borrador: PlantillaBorrador, id?: string): {
  resultado: ResultadoValidacionApi | null; huellaResultado: string | null; validando: boolean; error: string; reintentar: () => void;
} {
  const [resultado, setResultado] = useState<ResultadoValidacionApi | null>(null);
  const [huellaResultado, setHuellaResultado] = useState<string | null>(null);
  const [validando, setValidando] = useState(true);
  const [error, setError] = useState("");
  // Cada reintento suma 1: entra en la dependencia del efecto y dispara la misma validación.
  const [intento, setIntento] = useState(0);
  const reintentar = useCallback(() => setIntento((n) => n + 1), []);
  const seq = useRef(0);
  const borradorRef = useRef(borrador);
  borradorRef.current = borrador;

  const huella = huellaBorrador(borrador);
  const clave = `${id ?? ""}|${huella}`;

  useEffect(() => {
    let vivo = true;
    const mio = ++seq.current;
    setValidando(true);
    const t = setTimeout(async () => {
      try {
        const r = await oficialApi.validarPlantilla({ ...borradorRef.current, id });
        if (!vivo || mio !== seq.current) return;
        const l = leerRespuesta<ResultadoValidacionApi>(r);
        if (!l.ok) {
          // validar responde siempre 200 con `ok`: un 422 acá es un bug del backend y se muestra tal cual.
          setError(textoError(l));
        } else if (!l.data) {
          setError("El servidor no devolvió el resultado de la validación.");
        } else {
          setResultado(l.data);
          setHuellaResultado(huella);
          setError("");
        }
      } catch (e) {
        if (!vivo || mio !== seq.current) return;
        setError(textoError(leerError(e)));
      } finally {
        if (vivo && mio === seq.current) setValidando(false);
      }
    }, DEBOUNCE_VALIDACION_MS);
    return () => { vivo = false; clearTimeout(t); };
    // `clave` resume borrador (por huella) e id: un objeto nuevo con el mismo contenido no revalida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, intento]);

  return { resultado, huellaResultado, validando, error, reintentar };
}
